import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { transformSlashDate } from '@app/utils';
import { Namespace } from '@data/index';
import { TrialView } from '@data/sdk/match';
import { AntdSpace, AntdTypography } from '@ui/components';
import { OverallStatus } from './components';

const { Text } = AntdTypography;

type TrialProps = {
  trial: TrialView;
};

export const Trial = ({ trial }: TrialProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const location = useLocation();

  return (
    <li className="relative">
      <Link
        state={{ from: location, trial }}
        to={trial.id}
        className="flex flex-col gap-4 p-4 rounded-default w-full border border-solid border-base-400 mb-2 hover:bg-primary-100 group transition-all duration-300"
      >
        <Text className="text-base font-semibold group-hover:text-primary-600">
          {trial.trialId}: {trial.briefTitle}
        </Text>

        <div className="flex items-center gap-4 flex-wrap 2xl:flex-nowrap">
          <AntdSpace orientation="vertical" size={0} className="min-w-[60px]">
            <Text className="text-base-600">{t('trials_library.trial.matches')}</Text>
            <Text>{trial.confirmedUserTrialCount}</Text>
          </AntdSpace>
          <AntdSpace orientation="vertical" size={0} className="min-w-[160px]">
            <Text className="text-base-600">
              {t('trials_library.trial.patient_driven_matches')}
            </Text>
            <Text>{trial.pendingUserTrialCount}</Text>
          </AntdSpace>
          <AntdSpace orientation="vertical" size={0} className="min-w-[120px]">
            <Text className="text-base-600">{t('trials_library.trial.potential_patients')}</Text>
            <Text>{trial.potentialUserTrialCount}</Text>
          </AntdSpace>
          <AntdSpace orientation="vertical" size={0} className="min-w-[100px]">
            <Text className="text-base-600">{t('trials_library.trial.last_update')}</Text>
            <Text>{transformSlashDate(trial.updatedAt)}</Text>
          </AntdSpace>
          <AntdSpace orientation="vertical" size={0} className="min-w-[180px]">
            <Text className="text-base-600">{t('trials_library.trial.recruitment_period')}</Text>
            {trial.studyCompletionDate || trial.primaryCompletionDate ? (
              <Text>
                {transformSlashDate(trial.updatedAt)} -{' '}
                {transformSlashDate(
                  trial.studyCompletionDate || trial.primaryCompletionDate || new Date(),
                )}
              </Text>
            ) : (
              <>-</>
            )}
          </AntdSpace>
          {/* <AntdSpace orientation="vertical" size={0}>
            <Text className="text-base-600">{t('trials_library.trial.status')}</Text>
            <AppStatus status={trial.matchingStatus} />
          </AntdSpace> */}
          <AntdSpace orientation="vertical" size={0} className="min-w-[160px]">
            <Text className="text-base-600">{t('trials_library.trial.ct_status')}</Text>
            <OverallStatus status={trial.overallStatus} />
          </AntdSpace>
          <AntdSpace orientation="vertical" size={0} className="min-w-[120px]">
            <Text className="text-base-600">{t('trials_library.trial.locations_count')}</Text>
            <Text>{trial.locationsCount}</Text>
          </AntdSpace>
          <AntdSpace orientation="vertical" size={0} className="min-w-[120px] w-full">
            <Text className="text-base-600">{t('trials_library.trial.sponsor')}</Text>
            <Text className="max-w-[200px] break-words">{trial.sponsors?.[0]?.name}</Text>
          </AntdSpace>
        </div>
      </Link>
    </li>
  );
};
